import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { makeObservable, observable, runInAction } from "mobx";
import {
    LLMCLassifierDetailsResponse,
    LLMClassifierForLibrary,
    LLMClassifierGrouping,
} from "models/LLMs";
import { ServiceError } from "services/BaseService";
import LLMClassifierLibraryService from "services/LLMClassifierLibraryService";
import { AuthStore } from "stores/AuthStore";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";

@AcxStore
export class LLMClassifierLibraryStore extends BaseStore {
    public static readonly Tasks = {
        GetLLMClassifierLibrary: "GetLLMClassifierLibrary",
        SingleAddFromLibrary: (llmClassifierId: string) =>
            `AddFromLibrary-${llmClassifierId}`,
        GroupAddFromLibrary: (groupId: string, includeChildGroups = false) =>
            `AddFromLibrary-${groupId}-${includeChildGroups ? "children" : "direct"}`,
        AddToLibrary: "AddToLibrary",
        RemoveFromLibrary: "RemoveFromLibrary",
    } as const;

    @observable
    public library: LLMClassifierGrouping[] = [];

    private readonly messageStore: MessageStore;
    private readonly authStore: AuthStore;

    private readonly llmClassifierLibraryService: LLMClassifierLibraryService;

    constructor(private rootStore: IRootStore) {
        super("LLMClassifierLibraryStore");
        makeObservable(this);

        this.messageStore = this.rootStore.getStore(MessageStore);
        this.authStore = this.rootStore.getStore(AuthStore);

        this.llmClassifierLibraryService = new LLMClassifierLibraryService();
    }

    private logError(error: any) {
        if (error instanceof ServiceError) {
            return this.messageStore.logError(
                error.errorMessage ?? error.message,
            );
        }

        this.messageStore.logError(error);
    }

    public loadLibrary() {
        this.setupAsyncTask(
            LLMClassifierLibraryStore.Tasks.GetLLMClassifierLibrary,
            async () => {
                const response =
                    await this.llmClassifierLibraryService.getLLMClassifierLibrary();
                this.library = response.library;
            },
        );
    }

    public addFromLibrary(classifier: LLMClassifierForLibrary) {
        this.setupAsyncTask(
            LLMClassifierLibraryStore.Tasks.SingleAddFromLibrary(classifier.id),
            async () => {
                try {
                    await this.llmClassifierLibraryService.addFromLibrary(
                        classifier.id,
                    );
                    runInAction(() => {
                        classifier.existsInTenant = true;
                    });
                    this.messageStore.logMessage(
                        "Model added to Your Models",
                        "success",
                    );
                } catch (error) {
                    this.logError(error);
                }
            },
        );
    }

    public addGroupFromLibrary(groupId: string, includeChildGroups = false) {
        this.setupAsyncTask(
            LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                groupId,
                includeChildGroups,
            ),
            async () => {
                try {
                    await this.llmClassifierLibraryService.addFromLibraryGroup(
                        groupId,
                        includeChildGroups,
                    );
                    runInAction(() => {
                        const group = this.findGroup(groupId);
                        if (!group) return;

                        LLMClassifierLibraryStore.getClassifiersForGroup(
                            group,
                            includeChildGroups,
                        ).forEach((c) => {
                            c.existsInTenant = true;
                        });
                    });
                    this.messageStore.logMessage(
                        "Models added to Your Models",
                        "success",
                    );
                } catch (error) {
                    this.logError(error);
                }
            },
        );
    }

    public addToLibrary(classifier: LLMCLassifierDetailsResponse) {
        if (!this.authStore.isUserUltra()) {
            return this.messageStore.logError(
                "You do not have permission to add models to the library",
            );
        }

        if (!this.authStore.orgStore.isCurrentOrganizationAuthcx()) {
            return this.messageStore.logError(
                "Models can only be added to the library from the Authcx organization",
            );
        }

        this.setupAsyncTask(
            LLMClassifierLibraryStore.Tasks.AddToLibrary,
            async () => {
                try {
                    await this.llmClassifierLibraryService.addToLibrary(
                        classifier.id,
                    );
                    runInAction(() => {
                        classifier.createdBy = "AuthenticxClassifierLibrary";
                    });
                    this.messageStore.logMessage(
                        "Model added to library",
                        "success",
                    );
                } catch (error) {
                    this.logError(error);
                }
            },
        );
    }

    public removeFromLibrary(classifier: LLMCLassifierDetailsResponse) {
        this.setupAsyncTask(
            LLMClassifierLibraryStore.Tasks.RemoveFromLibrary,
            async () => {
                try {
                    await this.llmClassifierLibraryService.removeFromLibrary(
                        classifier.id,
                    );
                    runInAction(() => {
                        classifier.createdBy =
                            this.authStore._user.profile.email;
                    });
                    this.messageStore.logMessage(
                        "Model removed from library",
                        "success",
                    );
                } catch (error) {
                    this.logError(error);
                }
            },
        );
    }

    public static doesClassifierBelongToLibrary(
        classifier: LLMCLassifierDetailsResponse,
    ) {
        return classifier.createdBy === "AuthenticxClassifierLibrary";
    }

    public getAllLibraryModels() {
        return LLMClassifierLibraryStore.flattenClassifiers(this.library);
    }

    public findGroup(groupId: string) {
        return LLMClassifierLibraryStore.flattenGroups(this.library).find(
            (group) => group.groupId === groupId,
        );
    }

    public isGroupAddLoading(groupId: string) {
        return (
            this.getTaskLoading(
                LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                    groupId,
                    false,
                ),
            ) ||
            this.getTaskLoading(
                LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                    groupId,
                    true,
                ),
            )
        );
    }

    public isClassifierAffectedByGroupAddLoading(classifierId: string) {
        return LLMClassifierLibraryStore.flattenGroups(this.library).some(
            (group) => {
                const directLoading = this.getTaskLoading(
                    LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                        group.groupId,
                        false,
                    ),
                );
                const childLoading = this.getTaskLoading(
                    LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                        group.groupId,
                        true,
                    ),
                );

                return (
                    (directLoading &&
                        group.llmClassifiers.some(
                            (classifier) => classifier.id === classifierId,
                        )) ||
                    (childLoading &&
                        LLMClassifierLibraryStore.getClassifiersForGroup(
                            group,
                            true,
                        ).some((classifier) => classifier.id === classifierId))
                );
            },
        );
    }

    public static flattenGroups(groups: LLMClassifierGrouping[]) {
        return groups.flatMap((group): LLMClassifierGrouping[] => [
            group,
            ...LLMClassifierLibraryStore.flattenGroups(group.subGroups ?? []),
        ]);
    }

    public static flattenClassifiers(groups: LLMClassifierGrouping[]) {
        return LLMClassifierLibraryStore.flattenGroups(groups).flatMap(
            (group) => group.llmClassifiers,
        );
    }

    public static getClassifiersForGroup(
        group: LLMClassifierGrouping,
        includeChildGroups: boolean,
    ) {
        if (!includeChildGroups) {
            return group.llmClassifiers;
        }

        return LLMClassifierLibraryStore.flattenClassifiers([group]);
    }
}
